import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { Flex } from '@coinflare/ui/core';
import { ChevronLeftIcon, ChevronRightIcon } from '@coinflare/ui/icons';

import { Button } from 'src/components/core';
import { useAppSelector } from 'src/hooks';

const LabelsWrapper = styled.div`
    display: -webkit-box;
    gap: 10px;
    padding: 0px 16px;
    overflow: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
`;

interface CategorySelectorProps {
    // eslint-disable-next-line no-unused-vars
    onChange: (category: string) => void;
    selectedCategory: string;
}

const CategorySelector = ({ selectedCategory, onChange }: CategorySelectorProps) => {
    const { configV2 } = useAppSelector((state) => state.globalData);
    const { t } = useTranslation();
    const { colors } = useTheme();
    const scrollRef = useRef<HTMLDivElement>(null);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(true);

    // Handle scroll
    const scroll = (scroll: number) => {
        scrollRef.current!.scrollBy({ left: scroll, behavior: 'smooth' });
    };

    // Check scroll position to show/hide buttons
    const handleScroll = () => {
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current!;
        setShowLeftButton(scrollLeft > 0);
        setShowRightButton(scrollLeft < scrollWidth - clientWidth - 50);
    };

    // Add event listener for scroll
    useEffect(() => {
        const container = scrollRef.current!;
        container.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial scroll position

        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <Flex gap={'10px'} alignItems={'center'} marginTop={'10px'}>
            {showLeftButton && (
                <Flex
                    onClick={() => scroll(-200)}
                    alignItems={'center'}
                    style={{ cursor: 'pointer' }}
                >
                    <ChevronLeftIcon color={colors.text.primary} />
                </Flex>
            )}
            <LabelsWrapper ref={scrollRef}>
                <Button
                    onClick={() => onChange('')}
                    variant={selectedCategory === '' ? 'contained' : 'text'}
                    size="small"
                    color={selectedCategory === '' ? colors.text.primary : colors.text.tertiary}
                    styles={{
                        background:
                            selectedCategory === '' ? `${colors.border.dark} !important` : '',
                    }}
                >
                    {t('all')}
                </Button>
                {configV2?.tokenCustomLabels?.map((label) => (
                    <Button
                        key={label.labelId}
                        onClick={() => onChange(label.labelValue)}
                        variant={selectedCategory === label.labelValue ? 'contained' : 'text'}
                        size="small"
                        color={
                            selectedCategory === label.labelValue
                                ? colors.text.primary
                                : colors.text.tertiary
                        }
                        styles={{
                            background:
                                selectedCategory === label.labelValue
                                    ? `${colors.border.dark} !important`
                                    : '',
                        }}
                    >
                        {label.labelValue}
                    </Button>
                ))}
            </LabelsWrapper>
            {showRightButton && (
                <Flex
                    onClick={() => scroll(200)}
                    alignItems={'center'}
                    style={{ cursor: 'pointer' }}
                >
                    <ChevronRightIcon color={colors.text.primary} />
                </Flex>
            )}
        </Flex>
    );
};

export default CategorySelector;
